/**
 * RollbackMechanism.js
 * Revert a successfully deployed patch on a device.
 * Requires rollbackCommand to be defined on the patch.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { api } from '../api';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const SEV_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a' };
const SEV_BG    = { critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7', low: '#dcfce7' };
const STS_COLOR = {
  success: '#16a34a', failed: '#dc2626', pending: '#d97706', in_progress: '#1d4ed8',
  rolled_back: '#7c3aed', rollback_pending: '#d97706', rollback_in_progress: '#1d4ed8',
  rollback_success: '#16a34a', rollback_failed: '#dc2626'
};
const STS_BG = {
  success: '#dcfce7', failed: '#fee2e2', pending: '#fef3c7', in_progress: '#dbeafe',
  rolled_back: '#ede9fe', rollback_pending: '#fef3c7', rollback_in_progress: '#dbeafe',
  rollback_success: '#dcfce7', rollback_failed: '#fee2e2'
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export default function RollbackMechanism({ user }) {
  const [statuses,  setStatuses]  = useState([]);
  const [history,   setHistory]   = useState([]);
  const [patches,   setPatches]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('deployments'); // deployments | history
  const [search,    setSearch]    = useState('');
  const [msg,       setMsg]       = useState({ text: '', type: '' });

  // Confirm modal
  const [confirm,   setConfirm]   = useState(null); // { deviceId, patchId, hostname, patchName, rollbackCommand, needsApproval }
  const [rolling,   setRolling]   = useState(false);
  const [justification, setJustification] = useState('');
  const [submittingApproval, setSubmittingApproval] = useState(false);

  // Edit rollback command modal
  const [editPatch, setEditPatch] = useState(null);
  const [editCmd,   setEditCmd]   = useState('');
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, hRes, pRes] = await Promise.all([
        axios.get('/api/patches/compliance', { headers: getHeaders() }),
        api.getRollbackHistory(),
        api.getAllPatches()
      ]);
      // Only show successful deployments (eligible for rollback)
      const details = cRes.data.details || [];
      setStatuses(details.filter(s => s.status === 'success'));
      setHistory(hRes.data);
      // Build patch map
      const map = {};
      (pRes.data || []).forEach(p => { map[p.id] = p; });
      setPatches(map);
    } catch (e) {
      flash('Failed to load: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  function openConfirm(s) {
    const patch = patches[s.patch?.id] || s.patch || {};
    const severity = patch.severity || 'medium';
    const needsApproval = ['critical', 'high'].includes(severity);
    setConfirm({
      deviceId:        s.device?.id || s.deviceId,
      patchId:         s.patch?.id  || s.patchId,
      hostname:        s.device?.hostname || '?',
      patchName:       patch.name || '?',
      patchVersion:    patch.version || '?',
      severity,
      needsApproval,
      rollbackCommand: patch.rollbackCommand || null
    });
  }

  async function doRollback() {
    if (!confirm) return;
    setRolling(true);
    try {
      await api.triggerRollback(confirm.deviceId, confirm.patchId, user?.username || 'admin');
      flash(`↩ Rollback queued for ${confirm.patchName} on ${confirm.hostname}`);
      setConfirm(null);
      setJustification('');
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setRolling(false);
    }
  }

  async function submitRollbackApproval() {
    if (!confirm || !justification.trim()) return;
    setSubmittingApproval(true);
    try {
      await api.createApproval({
        action: 'rollback',
        priority: confirm.severity === 'critical' ? 'critical' : 'high',
        justification,
        actionPayload: {
          deviceId:  confirm.deviceId,
          patchId:   confirm.patchId,
          patchName: confirm.patchName,
          deviceNames: [confirm.hostname],
          rollbackCommand: confirm.rollbackCommand,
        }
      });
      flash(`✔ Rollback approval request submitted for ${confirm.patchName} on ${confirm.hostname}. An admin must approve before rollback executes.`);
      setConfirm(null);
      setJustification('');
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setSubmittingApproval(false);
    }
  }

  async function saveRollbackCommand() {
    if (!editPatch) return;
    setSaving(true);
    try {
      await axios.put(`/api/patches/${editPatch.id}`,
        { rollbackCommand: editCmd },
        { headers: getHeaders() }
      );
      flash(`Rollback command saved for ${editPatch.name}`);
      setEditPatch(null);
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const filtered = statuses.filter(s => {
    if (!search) return true;
    return [s.device?.hostname, s.patch?.name, s.patch?.vendor]
      .join(' ').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">↩ Rollback Mechanism</div>
          <div className="page-subtitle">
            Revert successfully deployed patches on specific endpoints
          </div>
        </div>
        <button className="refresh-btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      <div className="alert info" style={{ marginBottom: 20 }}>
        ↩ <strong>Rollback reverts a patch on a specific device.</strong> The agent runs the
        patch's <code>rollbackCommand</code> (e.g. uninstall via msiexec). After rollback, the
        patch is re-queued as pending so it can be re-deployed if needed.
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'deployments', label: `✔ Deployed Patches (${statuses.length})` },
          { id: 'history',     label: `📋 Rollback History (${history.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#7c3aed' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #7c3aed' : '2px solid transparent',
            marginBottom: -2
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── DEPLOYMENTS TAB ── */}
      {tab === 'deployments' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <input className="form-input" style={{ maxWidth: 320 }}
              placeholder="Search device / patch..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">✔</div>
                Successfully Deployed Patches — eligible for rollback
              </div>
            </div>
            {loading ? <div className="loading">Loading...</div> : (
              <table>
                <thead>
                  <tr>
                    <th>Device</th><th>Patch</th><th>Version</th>
                    <th>Severity</th><th>Deployed</th>
                    <th>Rollback Cmd</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="empty">No successful deployments found.</td></tr>
                  )}
                  {filtered.map((s, i) => {
                    const patch = patches[s.patch?.id] || s.patch || {};
                    const hasCmd = !!patch.rollbackCommand;
                    return (
                      <tr key={i}>
                        <td><strong>{s.device?.hostname || '—'}</strong></td>
                        <td style={{ fontSize: 13 }}>{patch.name || '—'}</td>
                        <td style={{ fontSize: 12, color: '#94a3b8' }}>{patch.version || '—'}</td>
                        <td>
                          {patch.severity && (
                            <span className="badge" style={{ background: SEV_BG[patch.severity], color: SEV_COLOR[patch.severity] }}>
                              {patch.severity}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(s.completedAt)}</td>
                        <td>
                          {hasCmd ? (
                            <span className="badge success">✔ Defined</span>
                          ) : (
                            <span className="badge failed">✘ Not set</span>
                          )}
                        </td>
                        <td>
                          <div className="flex-gap">
                            {user?.role === 'admin' && hasCmd && (
                              <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-start' }}>
                                <button className="btn btn-sm btn-danger" onClick={() => openConfirm(s)}>
                                  ↩ Rollback
                                </button>
                                {['critical','high'].includes(patch.severity) && (
                                  <span style={{ fontSize:9, fontWeight:700, color:'#d97706',
                                    background:'#fef3c7', padding:'1px 6px', borderRadius:10 }}>
                                    ⚠ Approval required
                                  </span>
                                )}
                              </div>
                            )}
                            {user?.role === 'admin' && !hasCmd && (
                              <button className="btn btn-sm btn-outline"
                                onClick={() => { setEditPatch(patch); setEditCmd(''); }}>
                                + Add Command
                              </button>
                            )}
                            {user?.role !== 'admin' && (
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>Admin only</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              <div className="panel-title-icon">📋</div>
              Rollback History
            </div>
          </div>
          {history.length === 0 ? (
            <div className="empty">No rollbacks performed yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Device</th><th>Patch</th><th>Status</th>
                  <th>Triggered By</th><th>Triggered At</th><th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((s, i) => (
                  <tr key={i}>
                    <td><strong>{s.device?.hostname || '—'}</strong></td>
                    <td style={{ fontSize: 13 }}>{s.patch?.name || '—'}</td>
                    <td>
                      <span className="badge" style={{ background: STS_BG[s.status], color: STS_COLOR[s.status] }}>
                        {s.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{s.rollbackTriggeredBy || '—'}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(s.rollbackTriggeredAt)}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDateTime(s.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── ROLLBACK CONFIRM MODAL ── */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 16 }}>
              {confirm.needsApproval ? '🔐 Rollback Requires Approval' : '↩ Confirm Rollback'}
            </div>

            <div style={{ background: '#fef9f0', border: '1px solid #fde68a', borderRadius: 8,
              padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}>
                <strong>Device:</strong> {confirm.hostname}
              </div>
              <div style={{ fontSize: 13, marginBottom: 4 }}>
                <strong>Patch:</strong> {confirm.patchName} v{confirm.patchVersion}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:12,
                  background: confirm.severity==='critical'?'#fee2e2': confirm.severity==='high'?'#ffedd5':'#fef3c7',
                  color: confirm.severity==='critical'?'#dc2626': confirm.severity==='high'?'#ea580c':'#d97706' }}>
                  {confirm.severity} severity
                </span>
                {confirm.needsApproval && (
                  <span style={{ fontSize:11, fontWeight:700, background:'#fef3c7',
                    color:'#d97706', padding:'2px 8px', borderRadius:12 }}>
                    ⚠ Requires Admin Approval
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
                <strong>Rollback command:</strong>
                <code style={{ display: 'block', marginTop: 4, padding: '6px 8px',
                  background: '#f1f5f9', borderRadius: 4, fontSize: 11, wordBreak: 'break-all' }}>
                  {confirm.rollbackCommand}
                </code>
              </div>
            </div>

            {confirm.needsApproval ? (
              <>
                <div className="alert info" style={{ marginBottom: 16 }}>
                  🔐 <strong>{confirm.severity === 'critical' ? 'Critical' : 'High'} severity rollback requires admin approval</strong> before execution.
                  Your request will be queued in the Approval Matrix. An admin must review and approve it — the rollback will execute immediately upon approval.
                </div>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Justification for rollback *</label>
                  <textarea className="form-input" rows={3}
                    placeholder="Explain why this rollback is needed (e.g. patch caused application crash on LP000113)..."
                    value={justification}
                    onChange={e => setJustification(e.target.value)} />
                </div>
                <div className="flex-gap" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => { setConfirm(null); setJustification(''); }}>Cancel</button>
                  <button className="btn btn-primary" onClick={submitRollbackApproval}
                    disabled={submittingApproval || !justification.trim()}>
                    {submittingApproval ? '⟳ Submitting...' : '📋 Submit for Approval'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="alert warning" style={{ marginBottom: 16 }}>
                  ⚠ This will uninstall the patch from <strong>{confirm.hostname}</strong>.
                  The patch will be re-queued as pending after rollback.
                </div>
                <div className="flex-gap" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
                  <button className="btn btn-danger" onClick={doRollback} disabled={rolling}>
                    {rolling ? '⟳ Rolling back...' : '↩ Confirm Rollback'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── EDIT ROLLBACK COMMAND MODAL ── */}
      {editPatch && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 520,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 8 }}>
              Add Rollback Command
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>
              {editPatch.name} · {editPatch.vendor}
            </div>
            <div className="form-group">
              <label className="form-label">Rollback Command</label>
              <textarea
                className="form-input"
                rows={4}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                placeholder={'e.g. msiexec /x {PRODUCT-GUID} /quiet /norestart\nor: cmd /c "C:\\path\\uninstall.exe" /silent'}
                value={editCmd}
                onChange={e => setEditCmd(e.target.value)}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                This command runs on the endpoint to uninstall the patch.
                Use the same quoting rules as the install command.
              </div>
            </div>
            <div className="flex-gap" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setEditPatch(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRollbackCommand}
                disabled={saving || !editCmd.trim()}>
                {saving ? '⟳ Saving...' : 'Save Command'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
